import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { cortexAdminContract } from '@cortex/domain-admin-api';
import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { ContractRouterClient } from '@orpc/contract';
import { config } from '../config.js';

/**
 * cortex-admin web ↔ API の通信に使う oRPC 型付きクライアント。共有 contract
 * （@cortex/domain-admin-api）から型を導出するため、エンドポイント URL や手書きの
 * レスポンス型を介さず、API の入出力型がそのままクライアントに伝播する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business cortex-admin web から admin API を呼び出す oRPC client。contract-first で型安全に通信し、保護手続きの 401 時は Edge Auth start へ誘導する。
 * @graph-connects /rpc [calls] Cortex Admin API oRPC endpoint
 */

/**
 * API base URL から oRPC endpoint URL を解決する。
 * @param apiUrl Vite config の API base URL
 * @param origin relative URL 解決に使う browser origin
 * @returns oRPC RPC endpoint URL
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business absolute / relative の API base URL から /rpc endpoint を安定して組み立てる。
 * @graph-connects globalThis.location [references, via:origin] relative API URL 解決
 */
export function resolveRpcUrl(apiUrl: string, origin: string = globalThis.location.origin): string {
  const normalizedApiUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
  return new URL('rpc', new URL(normalizedApiUrl, origin)).toString();
}

/**
 * oRPC RPC link。cookie 認証のため credentials を include し、保護手続きの 401 は既存 REST
 * fetch wrapper と同じ Edge Auth / login 遷移に揃える。
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business cortex-admin API /rpc への credential 付き transport。
 * @graph-connects /rpc [calls] Cortex Admin API oRPC endpoint
 */
const link = new RPCLink({
  url: resolveRpcUrl(config.apiUrl),
  fetch: async (request, init) => {
    const response = await globalThis.fetch(request, { ...init, credentials: 'include' });
    if (response.status === 401 && !request.url.includes('/rpc/auth/')) {
      if (response.headers.get('X-Edge-Auth-Start')) {
        window.location.href = buildEdgeAuthStartUrl(window.location.href);
      } else if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return response;
  },
});

/**
 * Cortex Admin API の型付き oRPC client。
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business `rpcClient.dataViewer.listJobs()` のように feature.procedure で呼び出す contract-first client。
 * @graph-connects /rpc [calls] Cortex Admin API oRPC procedures
 */
export const rpcClient: ContractRouterClient<typeof cortexAdminContract> = createORPCClient(link);
